import axios, { type InternalAxiosRequestConfig } from "axios";
import { invalidateOnboardingStatus } from "@/services/onboardingStatusCache";

const API_URL = import.meta.env.VITE_API_URL;

let activeWorkspaceId: string | null = null;
let refreshRequest: Promise<string> | null = null;
let sessionExpiryHandled = false;

interface AuthSessionHandlers {
  refreshAccessToken: () => Promise<string>;
  onSessionExpired: (error: unknown) => void;
}

interface RetryableRequestConfig extends InternalAxiosRequestConfig {
  _retriedAfterRefresh?: boolean;
}

let authSessionHandlers: AuthSessionHandlers | null = null;

const UNAUTHENTICATED_PATHS = new Set([
  "/auth/jwt/create/",
  "/auth/jwt/refresh/",
  "/auth/users/",
  "/auth/users/activation/",
  "/auth/users/resend_activation/",
  "/auth/users/reset_password/",
  "/auth/users/reset_password_confirm/",
  "/auth/google-login/",
]);

const normalizePathname = (url: string): string => {
  if (url.startsWith("http://") || url.startsWith("https://")) {
    return new URL(url).pathname;
  }
  return url;
};

const isUnauthenticatedEndpoint = (url?: string): boolean => {
  if (!url) {
    return false;
  }

  const pathname = normalizePathname(url);
  const withoutApiPrefix = pathname.startsWith("/api/")
    ? pathname.slice(4)
    : pathname;

  return UNAUTHENTICATED_PATHS.has(withoutApiPrefix);
};

const hasAuthHeader = (authorizationHeader: unknown): boolean => {
  return typeof authorizationHeader === "string" && authorizationHeader.length > 0;
};

const api = axios.create({
  baseURL: `${API_URL}/api`,
  withCredentials: true,
});

api.interceptors.request.use((config) => {
  const defaultAuthHeader = api.defaults.headers.common.Authorization;
  const requestAuthHeader = config.headers?.Authorization;
  const requestWorkspaceHeader = config.headers?.["X-Workspace-ID"];
  const requestIsAuthenticated =
    hasAuthHeader(requestAuthHeader) || hasAuthHeader(defaultAuthHeader);

  if (
    requestIsAuthenticated &&
    activeWorkspaceId &&
    !requestWorkspaceHeader &&
    !isUnauthenticatedEndpoint(config.url)
  ) {
    config.headers = config.headers ?? {};
    config.headers["X-Workspace-ID"] = activeWorkspaceId;
  }

  return config;
});

/**
 * `402 billing_onboarding_required` invalidates the cached onboarding status
 * (AITS-590).
 *
 * Not merely a signal that our copy might be stale — those backend paths call
 * `activate_deferred_onboarding()` before answering, so the rejection itself
 * moves the user out of deferred mode and back to the domain step. Anything we
 * still hold describes the state *before* that write.
 *
 * Invalidates rather than clears: the user is mid-session on a rendered page,
 * and dropping the value outright would send the shell back to its loading
 * state over the top of it.
 */
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const response = error?.response;
    if (response?.status === 402 && response?.data?.code === "billing_onboarding_required") {
      invalidateOnboardingStatus();
    }

    const request = error?.config as RetryableRequestConfig | undefined;
    const handlers = authSessionHandlers;
    if (
      response?.status !== 401 ||
      !request ||
      request._retriedAfterRefresh ||
      isUnauthenticatedEndpoint(request.url) ||
      !handlers
    ) {
      return Promise.reject(error);
    }

    request._retriedAfterRefresh = true;

    try {
      refreshRequest ??= handlers.refreshAccessToken().finally(() => {
        refreshRequest = null;
      });

      const accessToken = await refreshRequest;
      sessionExpiryHandled = false;
      api.defaults.headers.common.Authorization = `JWT ${accessToken}`;
      request.headers.Authorization = `JWT ${accessToken}`;
      return api(request);
    } catch (refreshError) {
      if (!sessionExpiryHandled) {
        sessionExpiryHandled = true;
        handlers.onSessionExpired(refreshError);
      }
    }

    return Promise.reject(error);
  },
);

export const setAuthSessionHandlers = (handlers: AuthSessionHandlers | null): void => {
  authSessionHandlers = handlers;
  sessionExpiryHandled = false;
};

export const setActiveWorkspaceId = (id: string | number | null): void => {
  activeWorkspaceId = id !== null ? String(id) : null;
};

export default api;
